import { saveAs } from 'file-saver';

import { getFiles } from '@/apis/files/api';
import { Message } from '@/lib/types/chat';
import { customFetch } from '@/lib/utils';

import { ConversationFile } from './types';

/**
 * You might see functions in here that are also in chat_api.tsx
 * This is because chat_api.tsx is a redundant context that
 * should be slowly phased out!
 */

export const getMessagesByConversationId = async (
  conversationId: string,
  accessToken: string
): Promise<Message[]> => {
  const response = await customFetch(`/backend/conversations/${conversationId}/messages`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
    }
  });

  if (!response.ok) throw await response.json();

  return (await response.json()) as Message[];
};

export const getConversationFiles = async (
  conversationId: string,
  accessToken: string
): Promise<ConversationFile[]> => {
  const files = await getFiles({ accessToken, conversationId });
  return files.map((file) => ({
    relativePath: file.path,
    path: file.path,
    sizeBytes: file.size,
    contentType: file.content_type ?? null,
    updatedAt: file.last_modified
  }));
};

export const downloadConversationFilesArchive = async (
  conversationId: string,
  accessToken: string
): Promise<{ name: string }> => {
  const query = new URLSearchParams({ conversationId });
  const response = await customFetch(`/backend/files/archive?${query.toString()}`, {
    headers: {
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (!response.ok) {
    const data = (await response.json().catch(() => null)) as { error?: string } | null;
    throw new Error(data?.error || 'Failed to download conversation files');
  }

  const name = 'conversation-files.zip';
  saveAs(await response.blob(), name);
  return { name };
};
